import type { User } from "../../../../models";
import type { Company, Job, Deal } from "../../../../models";
import { ImportTableColumn } from "../../../../utilities/types";
import { FIELD_TO_PROPERTY_MAP, ValidationContext, isFieldVisible } from "../../../../utilities/jobValidation";
import { MRT_Row } from "material-react-table";

/**
 * Get column visibility based on company settings and user context
 */
export const getColumnVisibilityFromCompanySettings = (
	company?: Company,
	user?: User,
	deal?: Deal,
): Record<string, boolean> => {
	const visibility: Record<string, boolean> = {};

	// Create validation context for visibility checks
	const context: ValidationContext = {
		company,
		user,
		deal,
	};

	// Use centralized isFieldVisible for all fields
	Object.keys(FIELD_TO_PROPERTY_MAP).forEach((key) => {
		const fieldType = key as ImportTableColumn;
		visibility[fieldType] = isFieldVisible(fieldType, context);
	});

	return visibility;
};

/**
 * Check if all jobs have no attached files
 */
export function allJobsHaveNoFiles(jobs: Job[]) {
	for (const job of jobs) {
		if (job.files.boundaryGeoJSON?.features?.length) return false;
		if (job.files.zonesGeoJSON?.features?.length) return false;
		if (job.files.pointsGeoJSON?.features?.length) return false;
	}
	return true;
}

/**
 * Find a deal that matches the specified parameters
 */
export function findDeal(deals: Deal[], density: number, depth: number, labPackage: string, micros: number | string) {
	return deals.find((deal: Deal) => {
		const dealLabPackage = deal.testPackage;
		micros = parseFloat(micros.toString());
		return (
			deal.density == density &&
			deal.depth == depth &&
			deal.frequencyOfMicros == micros &&
			(dealLabPackage == labPackage ||
				((labPackage == "" || labPackage == "No test package specified") &&
					(dealLabPackage == "" || dealLabPackage == undefined)))
		);
	});
}

/**
 * Helper function to update all jobs in a group and their subRow caches
 * This ensures consistent group update behavior across all column types
 *
 * @param row - The MRT row representing the group
 * @param jobs - The current jobs array
 * @param updateFn - Function to update each job
 * @returns Updated jobs array
 */
export function updateJobGroup(row: MRT_Row<Job>, jobs: Job[], updateFn: (job: Job) => void): Job[] {
	const newJobs = [...jobs];

	// Update all jobs in the group using subRows
	if (row.subRows) {
		for (const subRow of row.subRows) {
			// Find the job in our jobs array that corresponds to this subRow
			const job = newJobs.find((j) => j.id === subRow.original.id);
			if (job) {
				updateFn(job);
			}
		}
	}

	return newJobs;
}

/**
 * Update subrows in a deal-based group when a field changes
 */
export function dealBasedSubRowUpdate(row: any, field: keyof Job, newValue: any, allDeals: Deal[]) {
	if (row.subRows) {
		for (const subRow of row.subRows) {
			const job = subRow._valuesCache as Job;
			if (subRow.original.importGrowerName === row.original.importGrowerName) {
				subRow._valuesCache[field] = newValue;
				subRow._valuesCache.dealIds = [
					(
						findDeal(
							allDeals,
							job.importDensity ?? 0,
							job.importDepth ?? 0,
							job.externalTestPackageId ?? "",
							job.externalFrequencyOfMicros ?? 0,
						)?.id || "custom"
					).toString(),
				];
			}
		}
	}
}
